import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Loader2, ShieldCheck, UserPlus, Trash2 } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

interface Dpo {
  user_id: string;
  email: string | null;
  full_name: string | null;
  is_self: boolean;
}

export function DPOTeamManager() {
  const { user } = useAuth();
  const [dpos, setDpos] = useState<Dpo[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("manage-dpo-role", {
      body: { action: "list" },
    });
    if (error) {
      toast.error("Falha ao listar Encarregados");
    } else {
      setDpos((data?.dpos as Dpo[]) ?? []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const handlePromote = async () => {
    const trimmed = email.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      toast.error("Informe um e-mail válido");
      return;
    }
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("manage-dpo-role", {
      body: { action: "promote", email: trimmed },
    });
    if (error || data?.error) {
      const msg = data?.error === "user_not_found"
        ? "Nenhum usuário com este e-mail. Peça que ele se cadastre antes."
        : "Falha ao promover usuário";
      toast.error(msg);
    } else {
      toast.success("Encarregado adicionado");
      setEmail("");
      void load();
    }
    setSubmitting(false);
  };

  const handleRevoke = async (userId: string) => {
    const { data, error } = await supabase.functions.invoke("manage-dpo-role", {
      body: { action: "revoke", user_id: userId },
    });
    if (error || data?.error) {
      const msg = data?.error === "cannot_remove_last_dpo"
        ? "Não é possível remover o último Encarregado"
        : "Falha ao revogar acesso";
      toast.error(msg);
    } else {
      toast.success("Acesso revogado");
      void load();
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserPlus className="w-4 h-4" /> Adicionar Encarregado
          </CardTitle>
          <CardDescription>
            O usuário precisa ter conta criada na plataforma. Ao ser promovido, ele passa a acessar este Painel.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col sm:flex-row gap-2">
          <div className="flex-1 space-y-1">
            <Label htmlFor="dpo-email">E-mail do usuário</Label>
            <Input
              id="dpo-email"
              type="email"
              placeholder="encarregado@empresa.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </div>
          <Button onClick={handlePromote} disabled={submitting || !email.trim()} className="sm:self-end">
            {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Promover
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="w-4 h-4" /> Encarregados ativos ({dpos.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-20 w-full" />
          ) : dpos.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum Encarregado cadastrado.</p>
          ) : (
            <ul className="divide-y">
              {dpos.map((d) => (
                <li key={d.user_id} className="py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">
                      {d.full_name ?? d.email ?? d.user_id.slice(0, 8)}
                      {d.is_self && <span className="text-xs text-muted-foreground ml-2">(você)</span>}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{d.email}</p>
                  </div>
                  {!d.is_self && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="ghost" className="text-destructive">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Revogar acesso de Encarregado?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {d.full_name ?? d.email} perderá acesso ao Painel DPO imediatamente.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction onClick={() => handleRevoke(d.user_id)}>
                            Revogar
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
